import { useIntl } from 'react-intl';
import { GoalTimeFrameEnum, GoalTypeEnum } from 'sonata-static-storage/lib/schemas/campaign/goal.schema';

import { EntityPacingStatus } from '../EntityPacingStatus';

import type { InsertionOrderPacingProgress } from 'sonata-static-storage/lib/common/common.types';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { EntityPacingStatusEntry } from '../EntityPacingStatus/PacingStatusTable/PacingStatusTable';

type Props = {
  currencyInfo: BaseCurrencyInterface;
  entityPacing: InsertionOrderPacingProgress | null;
  /** Fields fetched per line item id; used to hide deleted line items with no spend. */
  lineItemFields?: Record<string, { deleted?: boolean }>;
};

export function InsertionOrderPacingStatus({ currencyInfo, entityPacing, lineItemFields }: Props) {
  const { formatMessage } = useIntl();

  const overallBudgetGoal = entityPacing?.lineItemGoals?.find(
    g => g.type === GoalTypeEnum.budget && g.timeFrame === GoalTimeFrameEnum.overall
  );
  const overallBudgetByLineItemId = new Map(overallBudgetGoal?.lineItems?.map(li => [li.lineItemId, li.current]));

  const shouldHideLineItem = (lineItemId: string): boolean =>
    lineItemFields?.[lineItemId]?.deleted === true && overallBudgetByLineItemId.get(lineItemId) === 0;

  const lineItemGoalsPacing =
    entityPacing?.lineItemGoals?.map(lIG => {
      const lineItems = lIG.lineItems ?? [];
      // The pacing response carries a per-line-item target that the library type omits.
      const hiddenTarget = lineItems
        .filter(({ lineItemId }) => shouldHideLineItem(lineItemId))
        .reduce((sum, li) => sum + ((li as { target?: number }).target ?? 0), 0);

      return {
        current: lIG.current,
        currentPct: lIG.currentPct,
        expected: lIG.expected,
        expectedPct: lIG.expectedPct,
        lineItems: lineItems
          .filter(({ lineItemId }) => !shouldHideLineItem(lineItemId))
          .map(({ lineItemId: id, ...lineItem }) => ({
            id,
            ...lineItem,
            goalType: lineItem.type
          })),
        pacing: !!lIG.pacingStatus,
        pacingStatus: lIG.pacingStatus,
        // Hidden (deleted, unspent) line items no longer count toward the aggregate target
        target: lIG.target - hiddenTarget,
        timeFrame: lIG.timeFrame,
        type: lIG.type
      };
    }) || [];

  const flightsGoalsPacing: EntityPacingStatusEntry[] =
    entityPacing?.flights?.map(({ flightId: id, ...flight }) => ({
      ...flight,
      id
    })) || [];

  return (
    <>
      <EntityPacingStatus
        currencyInfo={currencyInfo}
        entityPacing={
          entityPacing
            ? {
                ...entityPacing,
                childrenPacingStatus: flightsGoalsPacing,
                id: entityPacing.insertionOrderId,
                name: formatMessage({
                  defaultMessage: 'Total Flights',
                  id: 'pacingStatusModal.content.insertionOrderTable.totalRowTitle'
                }),
                type: 'insertionOrder'
              }
            : { id: '', isComposed: true, pacings: [], type: 'insertionOrder' }
        }
        headerName={formatMessage({
          defaultMessage: 'Flight Name',
          id: 'pacingStatusModal.content.insertionOrderTable.headerName'
        })}
        includeDatesColumn
        isTopLevel
        title={formatMessage({
          defaultMessage: 'Flights Budget Goals',
          id: 'pacingStatusModal.content.insertionOrderTable.title'
        })}
      />
      {lineItemGoalsPacing.map((lineItemGoalPacing, index) => (
        <EntityPacingStatus
          // biome-ignore lint/suspicious/noArrayIndexKey: No other unique key available
          currencyInfo={currencyInfo}
          entityPacing={{
            ...lineItemGoalPacing,
            childrenPacingStatus: lineItemGoalPacing.lineItems,
            goalType: lineItemGoalPacing.type,
            id: `${(entityPacing as InsertionOrderPacingProgress)?.insertionOrderId}_lineItems`,
            name: formatMessage({
              defaultMessage: 'Total Budget',
              id: 'pacingStatusModal.content.campaignTable.totalRowTitle.budget'
            }),
            type: 'lineItem'
          }}
          headerName={formatMessage({
            defaultMessage: 'Line item Name',
            id: 'pacingStatusModal.content.campaignTable.headerName'
          })}
          includeDatesColumn={false}
          isTopLevel
          key={index}
          title={formatMessage({
            defaultMessage: 'Line Item Goals',
            id: 'pacingStatusModal.content.campaignTable.title'
          })}
        />
      ))}
    </>
  );
}
